import type { Workspace } from '@rhia/protocol';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspaces } from './api';

export type WorkspaceSwitcherProps = { current: Workspace };

export function WorkspaceSwitcher({ current }: WorkspaceSwitcherProps) {
  const { data: workspaces, isPending } = useWorkspaces();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const others = (workspaces ?? []).filter((w) => w.id !== current.id);
  const hasOthers = others.length > 0;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isPending && !workspaces}
        className="flex items-center gap-1 rounded border border-[color:var(--color-border)] px-2 py-1 text-sm font-medium hover:bg-[color:var(--color-muted)] disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {current.name}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          className="absolute left-0 top-full z-30 mt-1 min-w-[240px] max-w-[calc(100vw-2rem)] rounded border border-[color:var(--color-border)] bg-card py-1 shadow-lg"
          role="listbox"
        >
          <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-[color:var(--color-muted-foreground)]">
            Switch workspace
          </div>
          {hasOthers ? (
            others.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(`/w/${w.slug}`);
                }}
                className="block w-full truncate px-3 py-1.5 text-left text-sm hover:bg-[color:var(--color-muted)]"
                role="option"
                aria-selected={false}
              >
                {w.name}
                <span className="ml-2 font-mono text-[10px] text-[color:var(--color-muted-foreground)]">
                  {w.slug}
                </span>
              </button>
            ))
          ) : (
            <div className="px-3 py-1.5 text-xs text-[color:var(--color-muted-foreground)]">
              {isPending ? 'loading…' : 'No other workspaces.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
